import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Ticket, Calendar, Users, Gift, Pencil, Trash2, Copy, Eye, Filter, Printer, BarChart3, Download, FileSpreadsheet, Target } from 'lucide-react';
import { exportToCSV, exportToExcel, formatDateForExport, formatSubscriptionTier } from '@/utils/exportUtils';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { CampaignDashboard } from './CampaignDashboard';

interface PromotionalCode {
  id: string;
  code: string;
  description: string | null;
  reward_type: string;
  reward_value: number;
  reward_description: string | null;
  audience: 'user' | 'provider' | 'both';
  provider_subscription_months: number | null;
  expires_at: string | null;
  max_uses: number | null;
  max_uses_per_user: number | null;
  current_uses: number;
  is_active: boolean;
  campaign_name: string | null;
  created_at: string;
}

interface CodeFormData {
  code: string;
  description: string;
  reward_type: 'credits' | 'subscription_days' | 'discount_percentage' | 'custom' | 'provider_subscription_months';
  reward_value: number;
  reward_description: string;
  audience: 'user' | 'provider' | 'both';
  provider_subscription_months: string;
  expires_at: string;
  max_uses: string;
  max_uses_per_user: string;
  campaign_name: string;
  is_active: boolean;
}

const initialFormData: CodeFormData = {
  code: '',
  description: '',
  reward_type: 'credits',
  reward_value: 0,
  reward_description: '',
  audience: 'both',
  provider_subscription_months: '',
  expires_at: '',
  max_uses: '',
  max_uses_per_user: '1',
  campaign_name: '',
  is_active: true,
};

export const PromotionalCodeManager: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showRedemptionsDialog, setShowRedemptionsDialog] = useState(false);
  const [selectedCode, setSelectedCode] = useState<PromotionalCode | null>(null);
  const [formData, setFormData] = useState<CodeFormData>(initialFormData);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [campaignFilter, setCampaignFilter] = useState<string>('all');

  const { data: codes, isLoading } = useQuery({
    queryKey: ['promotional-codes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('promotional_codes')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as PromotionalCode[];
    }
  });

  const { data: redemptions, isLoading: redemptionsLoading } = useQuery({
    queryKey: ['promotional-redemptions', selectedCode?.id],
    queryFn: async () => {
      if (!selectedCode) return [];
      const { data, error } = await supabase
        .from('promotional_code_redemptions')
        .select('*, users:user_id(email, full_name, subscription_tier)')
        .eq('code_id', selectedCode.id)
        .order('redeemed_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!selectedCode
  });

  const getExportColumns = () => [
    { key: 'users.full_name', header: t('promoCodes.user') },
    { key: 'users.email', header: t('promoCodes.email') },
    {
      key: 'users.subscription_tier',
      header: t('promoCodes.subscriptionPlan'),
      formatter: (val: string | null) => formatSubscriptionTier(val)
    },
    {
      key: 'redeemed_at',
      header: t('promoCodes.redeemedAt'),
      formatter: (val: string) => formatDateForExport(val)
    },
    {
      key: 'reward_granted',
      header: t('promoCodes.rewardGranted'),
      formatter: (val: any) => val ? `${val.type}: ${val.amount || val.percentage || val.description}` : ''
    },
  ];

  const handleExportRedemptionsCSV = () => {
    if (!redemptions || redemptions.length === 0) {
      toast.error(t('promoCodes.noRedemptionsToExport'));
      return;
    }
    const filename = `redemptions_${selectedCode?.code || 'export'}`;
    exportToCSV(redemptions, getExportColumns(), filename);
    toast.success(t('promoCodes.exportSuccess'));
  };

  const handleExportRedemptionsExcel = async () => {
    if (!redemptions || redemptions.length === 0) {
      toast.error(t('promoCodes.noRedemptionsToExport'));
      return;
    }
    const filename = `redemptions_${selectedCode?.code || 'export'}`;
    await exportToExcel(redemptions, getExportColumns(), filename, t('promoCodes.redemptions'));
    toast.success(t('promoCodes.exportSuccess'));
  };

  const createMutation = useMutation({
    mutationFn: async (data: CodeFormData) => {
      const payload: any = {
        code: data.code.toUpperCase().trim(),
        description: data.description || null,
        reward_type: data.reward_type,
        reward_value: data.reward_type === 'provider_subscription_months' 
          ? parseInt(data.provider_subscription_months || '0') 
          : data.reward_value,
        reward_description: data.reward_type === 'custom' ? data.reward_description : null,
        audience: data.audience,
        provider_subscription_months: data.reward_type === 'provider_subscription_months' 
          ? parseInt(data.provider_subscription_months || '0') 
          : null,
        expires_at: data.expires_at || null,
        max_uses: data.max_uses ? parseInt(data.max_uses) : null,
        max_uses_per_user: data.max_uses_per_user ? parseInt(data.max_uses_per_user) : null,
        campaign_name: data.campaign_name || null,
        is_active: data.is_active,
      };

      if (editingId) {
        const { error } = await supabase
          .from('promotional_codes')
          .update(payload)
          .eq('id', editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('promotional_codes')
          .insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotional-codes'] });
      toast.success(editingId ? t('promoCodes.updated') : t('promoCodes.created'));
      resetForm();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('promotional_codes')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotional-codes'] });
      toast.success(t('promoCodes.deleted'));
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from('promotional_codes')
        .update({ is_active })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotional-codes'] });
    }
  });

  const resetForm = () => {
    setFormData(initialFormData);
    setEditingId(null);
    setShowCreateDialog(false);
  };

  const handleEdit = (code: PromotionalCode) => {
    setFormData({
      code: code.code,
      description: code.description || '',
      reward_type: code.reward_type as CodeFormData['reward_type'],
      reward_value: code.reward_value,
      reward_description: code.reward_description || '',
      audience: code.audience || 'both',
      provider_subscription_months: code.provider_subscription_months?.toString() || '',
      expires_at: code.expires_at ? code.expires_at.split('T')[0] : '',
      max_uses: code.max_uses?.toString() || '',
      max_uses_per_user: code.max_uses_per_user?.toString() || '1',
      campaign_name: code.campaign_name || '',
      is_active: code.is_active,
    });
    setEditingId(code.id);
    setShowCreateDialog(true);
  };

  const handleViewRedemptions = (code: PromotionalCode) => {
    setSelectedCode(code);
    setShowRedemptionsDialog(true);
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success(t('promoCodes.copied'));
  };

  const getRewardTypeLabel = (type: string) => {
    switch (type) {
      case 'credits': return t('promoCodes.rewardTypes.credits');
      case 'subscription_days': return t('promoCodes.rewardTypes.subscriptionDays');
      case 'provider_subscription_months': return t('promoCodes.rewardTypes.providerSubscriptionMonths');
      case 'discount_percentage': return t('promoCodes.rewardTypes.discountPercentage');
      case 'custom': return t('promoCodes.rewardTypes.custom');
      default: return type;
    }
  };

  const getRewardDisplay = (code: PromotionalCode) => {
    switch (code.reward_type) {
      case 'credits': return `${code.reward_value} ${t('promoCodes.credits')}`;
      case 'subscription_days': return `${code.reward_value} ${t('promoCodes.days')}`;
      case 'provider_subscription_months': return `${code.provider_subscription_months || code.reward_value} ${t('promoCodes.months')}`;
      case 'discount_percentage': return `${code.reward_value}%`;
      case 'custom': return code.reward_description || t('promoCodes.customReward');
      default: return code.reward_value.toString();
    }
  };

  // Get unique campaigns for filter
  const campaigns = Array.from(new Set(codes?.map(c => c.campaign_name).filter(Boolean) || []));

  // Filter codes by campaign
  const filteredCodes = codes?.filter(code => {
    if (campaignFilter === 'all') return true;
    if (campaignFilter === 'none') return !code.campaign_name;
    return code.campaign_name === campaignFilter;
  }) || [];

  // Calculate stats
  const totalCodes = filteredCodes.length;
  const totalRedemptions = filteredCodes.reduce((sum, c) => sum + c.current_uses, 0);
  const activeCodes = filteredCodes.filter(c => c.is_active).length;

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return <Skeleton className="h-96" />;
  }

  // Check if a specific campaign is selected (not 'all' or 'none')
  const isCampaignSelected = campaignFilter !== 'all' && campaignFilter !== 'none' && campaigns.includes(campaignFilter);

  return (
    <div className="space-y-4 pb-24 print:p-4">
      {/* Campaign Dashboard - Shows when a specific campaign is selected */}
      {isCampaignSelected && (
        <CampaignDashboard
          campaignName={campaignFilter}
          codes={filteredCodes}
        />
      )}

      {/* Stats Cards - Shows when no specific campaign is selected */}
      {!isCampaignSelected && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 print:grid-cols-3">
          <Card className="print:border print:shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Ticket className="h-4 w-4 text-primary" />
                {t('promoCodes.totalCodes')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{totalCodes}</p>
            </CardContent>
          </Card>
          <Card className="print:border print:shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-green-500" />
                {t('promoCodes.totalRedemptions')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{totalRedemptions}</p>
            </CardContent>
          </Card>
          <Card className="print:border print:shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-500" />
                {t('promoCodes.activeCodes')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{activeCodes}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="print:border print:shadow-none">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Ticket className="h-5 w-5 text-primary" />
            {t('promoCodes.title')}
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {/* Campaign Filter */}
            <div className="flex items-center gap-2 print:hidden">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={campaignFilter} onValueChange={setCampaignFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={t('promoCodes.filterByCampaign')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('promoCodes.allCampaigns')}</SelectItem>
                  <SelectItem value="none">{t('promoCodes.noCampaign')}</SelectItem>
                  {campaigns.map((campaign) => (
                    <SelectItem key={campaign} value={campaign || ''}>{campaign}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" onClick={handlePrint} className="print:hidden">
              <Printer className="h-4 w-4 mr-1" />
              {t('adminReferrals.print')}
            </Button>
            <Dialog open={showCreateDialog} onOpenChange={(open) => {
              if (!open) resetForm();
              setShowCreateDialog(open);
            }}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  {t('promoCodes.createCode')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingId ? t('promoCodes.editCode') : t('promoCodes.createCode')}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(formData); }} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t('promoCodes.code')} *</Label>
                      <Input
                        value={formData.code}
                        onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                        placeholder="PROMO2024"
                        className="font-mono"
                        required
                      />
                    </div>
                    <div>
                      <Label>{t('promoCodes.campaignLabel')}</Label>
                      <Input
                        value={formData.campaign_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, campaign_name: e.target.value }))}
                        placeholder={t('promoCodes.campaignPlaceholder')}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>{t('promoCodes.description')}</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder={t('promoCodes.descriptionPlaceholder')}
                      rows={2}
                    />
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Gift className="h-4 w-4" />
                      {t('promoCodes.rewardConfig')}
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <Label>{t('promoCodes.audience')} *</Label>
                        <Select
                          value={formData.audience}
                          onValueChange={(v) => setFormData(prev => ({ ...prev, audience: v as CodeFormData['audience'] }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">{t('promoCodes.audience.user')}</SelectItem>
                            <SelectItem value="provider">{t('promoCodes.audience.provider')}</SelectItem>
                            <SelectItem value="both">{t('promoCodes.audience.both')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>{t('promoCodes.rewardType')} *</Label>
                        <Select
                          value={formData.reward_type}
                          onValueChange={(v) => setFormData(prev => ({ ...prev, reward_type: v as CodeFormData['reward_type'] }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="credits">{t('promoCodes.rewardTypes.credits')}</SelectItem>
                            <SelectItem value="subscription_days">{t('promoCodes.rewardTypes.subscriptionDays')}</SelectItem>
                            <SelectItem value="provider_subscription_months">{t('promoCodes.rewardTypes.providerSubscriptionMonths')}</SelectItem>
                            <SelectItem value="discount_percentage">{t('promoCodes.rewardTypes.discountPercentage')}</SelectItem>
                            <SelectItem value="custom">{t('promoCodes.rewardTypes.custom')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Credits amount */}
                      {formData.reward_type === 'credits' && (
                        <div>
                          <Label>{t('promoCodes.creditsAmount')}</Label>
                          <Input
                            type="number"
                            value={formData.reward_value}
                            onChange={(e) => setFormData(prev => ({ ...prev, reward_value: parseFloat(e.target.value) || 0 }))}
                            min="0"
                          />
                        </div>
                      )}

                      {/* Subscription duration (days) for user subscription */}
                      {formData.reward_type === 'subscription_days' && (
                        <div>
                          <Label>{t('promoCodes.subscriptionDuration')}</Label>
                          <Input
                            type="number"
                            value={formData.reward_value}
                            onChange={(e) => setFormData(prev => ({ ...prev, reward_value: parseFloat(e.target.value) || 0 }))}
                            min="1"
                            placeholder="30"
                          />
                        </div>
                      )}

                      {/* Provider subscription months */}
                      {formData.reward_type === 'provider_subscription_months' && (
                        <div>
                          <Label>{t('promoCodes.providerSubscriptionMonths')}</Label>
                          <Input
                            type="number"
                            value={formData.provider_subscription_months}
                            onChange={(e) => setFormData(prev => ({ ...prev, provider_subscription_months: e.target.value, reward_value: parseInt(e.target.value) || 0 }))}
                            min="1"
                            placeholder="3"
                          />
                        </div>
                      )}

                      {/* Discount percentage */}
                      {formData.reward_type === 'discount_percentage' && (
                        <div>
                          <Label>{t('promoCodes.percentage')}</Label>
                          <Input
                            type="number"
                            value={formData.reward_value}
                            onChange={(e) => setFormData(prev => ({ ...prev, reward_value: parseFloat(e.target.value) || 0 }))}
                            min="0"
                            max={100}
                          />
                        </div>
                      )}

                      {formData.reward_type === 'custom' && (
                        <div>
                          <Label>{t('promoCodes.customDescription')} *</Label>
                          <Textarea
                            value={formData.reward_description}
                            onChange={(e) => setFormData(prev => ({ ...prev, reward_description: e.target.value }))}
                            placeholder={t('promoCodes.customDescriptionPlaceholder')}
                            required={formData.reward_type === 'custom'}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {t('promoCodes.validityConfig')}
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>{t('promoCodes.expiresAt')}</Label>
                        <Input
                          type="date"
                          value={formData.expires_at}
                          onChange={(e) => setFormData(prev => ({ ...prev, expires_at: e.target.value }))}
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      <div>
                        <Label>{t('promoCodes.maxUses')}</Label>
                        <Input
                          type="number"
                          value={formData.max_uses}
                          onChange={(e) => setFormData(prev => ({ ...prev, max_uses: e.target.value }))}
                          placeholder={t('promoCodes.unlimited')}
                          min="1"
                        />
                      </div>
                      <div>
                        <Label>{t('promoCodes.maxUsesPerUser')}</Label>
                        <Input
                          type="number"
                          value={formData.max_uses_per_user}
                          onChange={(e) => setFormData(prev => ({ ...prev, max_uses_per_user: e.target.value }))}
                          placeholder="1"
                          min="1"
                        />
                      </div>
                      <div className="flex items-center gap-2 pt-5">
                        <Switch
                          checked={formData.is_active}
                          onCheckedChange={(v) => setFormData(prev => ({ ...prev, is_active: v }))}
                        />
                        <Label>{t('promoCodes.activeStatus')}</Label>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={resetForm} className="flex-1">
                      {t('common.cancel')}
                    </Button>
                    <Button type="submit" className="flex-1" disabled={createMutation.isPending}>
                      {createMutation.isPending ? t('common.processing') : (editingId ? t('common.save') : t('promoCodes.createCode'))}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {filteredCodes && filteredCodes.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                  <TableHead>{t('promoCodes.code')}</TableHead>
                    <TableHead>{t('promoCodes.reward')}</TableHead>
                    <TableHead>{t('promoCodes.audience')}</TableHead>
                    <TableHead>{t('promoCodes.campaignLabel')}</TableHead>
                    <TableHead className="text-center">{t('promoCodes.uses')}</TableHead>
                    <TableHead>{t('promoCodes.expires')}</TableHead>
                    <TableHead>{t('promoCodes.status')}</TableHead>
                    <TableHead className="text-right">{t('promoCodes.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {codes.map((code) => (
                    <TableRow key={code.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="font-mono cursor-pointer" onClick={() => copyCode(code.code)}>
                            {code.code}
                            <Copy className="h-3 w-3 ml-1 opacity-50" />
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">{getRewardDisplay(code)}</span>
                          <span className="text-xs text-muted-foreground">{getRewardTypeLabel(code.reward_type)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs">
                          {t(`promoCodes.audience.${code.audience || 'both'}`)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{code.campaign_name || '-'}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-mono">
                          {code.current_uses}/{code.max_uses || '∞'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {code.expires_at ? (
                          <span className={`text-sm ${new Date(code.expires_at) < new Date() ? 'text-destructive' : ''}`}>
                            {format(new Date(code.expires_at), 'dd/MM/yyyy')}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={code.is_active}
                          onCheckedChange={(v) => toggleActiveMutation.mutate({ id: code.id, is_active: v })}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleViewRedemptions(code)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(code)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => {
                              if (confirm(t('promoCodes.deleteConfirm'))) {
                                deleteMutation.mutate(code.id);
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Ticket className="h-12 w-12 mx-auto mb-2 opacity-20" />
              <p>{t('promoCodes.noCodes')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Redemptions Dialog */}
      <Dialog open={showRedemptionsDialog} onOpenChange={setShowRedemptionsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t('promoCodes.redemptions')} - {selectedCode?.code}
              </DialogTitle>
              {redemptions && redemptions.length > 0 && (
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" onClick={handleExportRedemptionsCSV}>
                    <Download className="h-4 w-4 mr-1" />
                    CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleExportRedemptionsExcel}>
                    <FileSpreadsheet className="h-4 w-4 mr-1" />
                    Excel
                  </Button>
                </div>
              )}
            </div>
          </DialogHeader>
          {redemptionsLoading ? (
            <Skeleton className="h-48" />
          ) : redemptions && redemptions.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('promoCodes.user')}</TableHead>
                  <TableHead>{t('promoCodes.redeemedAt')}</TableHead>
                  <TableHead>{t('promoCodes.rewardGranted')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {redemptions.map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{r.users?.full_name || t('promoCodes.unknownUser')}</div>
                        <div className="text-xs text-muted-foreground">{r.users?.email}</div>
                      </div>
                    </TableCell>
                    <TableCell>{format(new Date(r.redeemed_at), 'dd/MM/yyyy HH:mm')}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {r.reward_granted?.type}: {r.reward_granted?.amount || r.reward_granted?.percentage || r.reward_granted?.description}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center py-8 text-muted-foreground">{t('promoCodes.noRedemptions')}</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
